import React from 'react';
import { DocumentAnnotation } from '../../types';

interface AnnotationHighlightLayerProps {
  pageNumber: number;
  annotations: DocumentAnnotation[];
  activeAnnotationFlashId?: string | null;
  activeAnnotationId?: string | null;
  draftAnnotation?: {
    pageNumber: number;
    rectangles: { x: number; y: number; width: number; height: number }[];
    colorHex?: string;
  } | null;
  onAnnotationClick: (annotation: DocumentAnnotation, e: React.MouseEvent) => void;
}

export const AnnotationHighlightLayer: React.FC<AnnotationHighlightLayerProps> = ({
  pageNumber,
  annotations,
  activeAnnotationFlashId,
  activeAnnotationId,
  draftAnnotation,
  onAnnotationClick,
}) => {
  // État local pour tracer l'annotation actuellement survolée à la souris
  const [hoveredAnnoId, setHoveredAnnoId] = React.useState<string | null>(null);

  // Filtrer les annotations strictement valides pour cette page ayant au moins un rectangle
  const pageAnnotations = React.useMemo(() => {
    return annotations.filter(
      (a) => a.pageNumber === pageNumber && Array.isArray(a.rectangles) && a.rectangles.length > 0
    );
  }, [annotations, pageNumber]);

  const isDraftOnThisPage = Boolean(
    draftAnnotation &&
    draftAnnotation.pageNumber === pageNumber &&
    Array.isArray(draftAnnotation.rectangles) &&
    draftAnnotation.rectangles.length > 0
  );

  if (pageAnnotations.length === 0 && !isDraftOnThisPage) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none z-15 overflow-hidden select-none"
      aria-hidden="true"
    >
      {pageAnnotations.map((annotation) => {
        const isFlashing = activeAnnotationFlashId === annotation.id;
        const isActive = activeAnnotationId === annotation.id || isFlashing;
        const isHovered = hoveredAnnoId === annotation.id;
        const color = annotation.colorHex || '#F59E0B';

        // Styles dynamiques selon état : Actif > Survolé (Hover) > Normal
        let bgStyle = `${color}22`; // ~13% par défaut
        let borderStyle = `2px solid ${color}`;
        let shadowStyle = `0 1px 4px ${color}35`;
        let zIndex = 15;
        let transformStyle = 'none';

        if (isActive) {
          bgStyle = `${color}40`; // ~25% plus marqué
          borderStyle = `3px solid ${color}`;
          shadowStyle = `0 0 0 3px ${color}80, 0 0 20px ${color}cc, 0 4px 12px rgba(0,0,0,0.25)`;
          zIndex = 40;
          transformStyle = 'scale(1.015)';
        } else if (isHovered) {
          bgStyle = `${color}35`; // ~21% intensifié au survol avec SA couleur de base
          borderStyle = `2.5px solid ${color}`;
          shadowStyle = `0 0 14px ${color}90, 0 0 0 1px ${color}`;
          zIndex = 30;
          transformStyle = 'scale(1.008)';
        }

        return (
          <React.Fragment key={`anno_group_${annotation.id}`}>
            {annotation.rectangles.map((rect, idx) => {
              // Sécurisation des pourcentages entre 0 et 100
              const left = Math.max(0, Math.min(100, rect.x));
              const top = Math.max(0, Math.min(100, rect.y));
              const width = Math.max(0.5, Math.min(100 - left, rect.width));
              const height = Math.max(0.5, Math.min(100 - top, rect.height));

              return (
                <div
                  key={`anno_rect_${annotation.id}_${idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAnnotationClick(annotation, e);
                  }}
                  onMouseEnter={() => setHoveredAnnoId(annotation.id)}
                  onMouseLeave={() => setHoveredAnnoId(null)}
                  title={annotation.noteText ? `Note : ${annotation.noteText}` : annotation.selectedText}
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                    backgroundColor: bgStyle,
                    border: borderStyle,
                    borderRadius: '6px',
                    boxShadow: shadowStyle,
                    zIndex,
                    transform: transformStyle,
                  }}
                  className={`absolute pointer-events-auto cursor-pointer transition-all duration-200 group ${
                    isFlashing ? 'animate-pulse' : ''
                  }`}
                >
                  {/* Badge d'annotation en haut à droite avec état Actif / Hover */}
                  {isActive ? (
                    <div
                      className="absolute -top-3 -right-2 px-1.5 h-4.5 rounded-full flex items-center justify-center gap-1 text-white shadow-md z-50 animate-in fade-in zoom-in-95 duration-150"
                      style={{
                        backgroundColor: color,
                        boxShadow: `0 0 10px ${color}`,
                      }}
                    >
                      <span className="text-[8.5px] font-black leading-none">✓</span>
                      <span className="text-[7.5px] font-extrabold uppercase tracking-wider">Actif</span>
                    </div>
                  ) : (
                    <div
                      className="absolute -top-2 -right-2 w-4 h-4 rounded-full flex items-center justify-center text-white shadow-xs transition-all duration-150"
                      style={{
                        backgroundColor: color,
                        boxShadow: isHovered ? `0 0 8px ${color}` : undefined,
                        transform: isHovered ? 'scale(1.15)' : 'scale(1)',
                        opacity: isHovered ? 1 : 0.8,
                      }}
                    >
                      <span className="text-[8px] font-black leading-none">✓</span>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}

      {/* Rendu persistant du cadre en cours de saisie / description (Draft) */}
      {isDraftOnThisPage && draftAnnotation && (
        <React.Fragment key={`draft_box_${pageNumber}`}>
          {draftAnnotation.rectangles.map((rect, idx) => {
            const left = Math.max(0, Math.min(100, rect.x));
            const top = Math.max(0, Math.min(100, rect.y));
            const width = Math.max(0.5, Math.min(100 - left, rect.width));
            const height = Math.max(0.5, Math.min(100 - top, rect.height));
            const color = draftAnnotation.colorHex || '#F59E0B';

            return (
              <div
                key={`draft_rect_${idx}`}
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                  backgroundColor: `${color}30`,
                  border: `2.5px dashed ${color}`,
                  borderRadius: '6px',
                  boxShadow: `0 0 0 3px ${color}60, 0 0 20px ${color}90`,
                  zIndex: 45,
                }}
                className="absolute pointer-events-none animate-pulse"
              >
                {/* Badge visible en haut à droite indiquant l'encadré en cours */}
                <div
                  className="absolute -top-3.5 -right-2 px-2 py-0.5 rounded-full flex items-center justify-center gap-1 text-zinc-950 font-black text-[8px] shadow-lg z-50 uppercase tracking-wider animate-bounce"
                  style={{
                    backgroundColor: color,
                    boxShadow: `0 0 10px ${color}`,
                  }}
                >
                  <span>Encadré actif</span>
                </div>
              </div>
            );
          })}
        </React.Fragment>
      )}
    </div>
  );
};
